import React, { useState, useRef, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import {
  GitMerge,
  Check,
  X,
  RotateCcw,
  Eye,
  EyeOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DragConflict, UserSession } from "@/stores/collaboration";
import { Component } from "@/types";
import { UserAvatar } from "./UserAvatar";

interface MergeEditorProps {
  conflict: DragConflict;
  originalComponent: Component;
  conflictedVersions: Array<{
    component: Component;
    userId: string;
    user: UserSession;
    timestamp: Date;
    changes: Array<{
      field: string;
      oldValue: unknown;
      newValue: unknown;
    }>;
  }>;
  onResolve: (
    mergedComponent: Component,
    resolution: "merge" | "manual",
  ) => void;
  onCancel: () => void;
  className?: string;
}

export const MergeEditor: React.FC<MergeEditorProps> = ({
  conflict: _conflict,
  originalComponent,
  conflictedVersions,
  onResolve,
  onCancel,
  className = "",
}) => {
  const [mergedComponent, setMergedComponent] =
    useState<Component>(originalComponent);
  const [selectedFields, setSelectedFields] = useState<Map<string, string>>(
    new Map(),
  ); // field -> userId
  const [showDiff, setShowDiff] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  // Get all changed fields across all versions (memoized for performance)
  const changedFields = useMemo(() => {
    const fields = new Set<string>();
    conflictedVersions.forEach((version) => {
      version.changes.forEach((change) => {
        fields.add(change.field);
      });
    });
    return Array.from(fields);
  }, [conflictedVersions]);

  // Auto-resolve non-conflicting changes
  useEffect(() => {
    const autoResolvedComponent = { ...originalComponent };
    const newSelectedFields = new Map<string, string>();

    changedFields.forEach((field) => {
      // Get all versions that changed this field
      const versionsWithChange = conflictedVersions.filter((version) =>
        version.changes.some((change) => change.field === field),
      );

      if (versionsWithChange.length === 1) {
        // No conflict - auto-select this version
        const version = versionsWithChange[0];
        const change = version.changes.find((c) => c.field === field);
        if (change) {
          (autoResolvedComponent as any)[field] = change.newValue;
          newSelectedFields.set(field, version.userId);
        }
      } else {
        // Conflict - select the most recent change by default
        const mostRecent = versionsWithChange.reduce((latest, current) =>
          current.timestamp > latest.timestamp ? current : latest,
        );
        const change = mostRecent.changes.find((c) => c.field === field);
        if (change) {
          (autoResolvedComponent as any)[field] = change.newValue;
          newSelectedFields.set(field, mostRecent.userId);
        }
      }
    });

    setMergedComponent(autoResolvedComponent);
    setSelectedFields(newSelectedFields);
  }, [originalComponent, conflictedVersions, changedFields]);

  // Handle field value selection
  const handleFieldSelection = (field: string, userId: string, value: unknown) => {
    setSelectedFields((prev) => new Map(prev).set(field, userId));
    setMergedComponent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Reset to original
  const handleReset = () => {
    setMergedComponent(originalComponent);
    setSelectedFields(new Map());
  };


  // Check if a field has conflicts
  const hasFieldConflict = (field: string): boolean => {
    return (
      conflictedVersions.filter((version) =>
        version.changes.some((change) => change.field === field),
      ).length > 1
    );
  };

  // Render field value with highlighting
  const renderFieldValue = (
    value: unknown,
    isSelected: boolean = false,
  ): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">null</span>;
    }

    if (typeof value === "object") {
      return (
        <pre
          className={`text-xs p-2 rounded ${isSelected ? "bg-blue-50" : "bg-gray-50"} overflow-x-auto`}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      );
    }

    return (
      <span
        className={`font-mono text-sm ${isSelected ? "text-blue-700 font-medium" : "text-gray-700"}`}
      >
        {String(value)}
      </span>
    );
  };

  // Render diff for a field
  const renderFieldDiff = (field: string) => {
    const originalValue = (originalComponent as any)[field];
    const conflictingVersions = conflictedVersions.filter((version) =>
      version.changes.some((change) => change.field === field),
    );

    if (conflictingVersions.length === 0) return null;

    const selectedUserId = selectedFields.get(field);
    const hasConflict = hasFieldConflict(field);

    return (
      <div key={field} className="border rounded-lg p-4 space-y-3">
        {/* Field header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm text-gray-900">{field}</h4>
            {hasConflict && (
              <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">
                Conflict
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500">
            {conflictingVersions.length} version
            {conflictingVersions.length > 1 ? "s" : ""}
          </div>
        </div>

        {/* Original value */}
        <div className="bg-gray-50 p-3 rounded">
          <div className="text-xs text-gray-600 mb-2 flex items-center gap-1">
            <RotateCcw className="w-3 h-3" />
            Original
          </div>
          {renderFieldValue(originalValue)}
        </div>

        {/* Conflicted versions */}
        <div className="space-y-2">
          {conflictingVersions.map((version) => {
            const change = version.changes.find((c) => c.field === field);
            if (!change) return null;

            const isSelected = selectedUserId === version.userId;

            return (
              <motion.div
                key={version.userId}
                className={`border-2 p-3 rounded cursor-pointer transition-all ${
                  isSelected
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                }`}
                onClick={() =>
                  handleFieldSelection(field, version.userId, change.newValue)
                }
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <UserAvatar
                      user={version.user}
                      size="sm"
                      showStatus={false}
                    />
                    <div>
                      <div className="text-sm font-medium">
                        {version.user.user?.username || `User ${version.user.userId?.slice(-4)}`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {version.timestamp.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                </div>
                {renderFieldValue(change.newValue, isSelected)}
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      ref={containerRef}
      className={`fixed inset-4 bg-white rounded-lg shadow-2xl z-50 flex flex-col ${className}`}
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      transition={{ type: "spring", damping: 25, stiffness: 300 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gray-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <GitMerge className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Resolve Merge Conflict
            </h2>
            <p className="text-sm text-gray-600">
              Component: {originalComponent.name} (
              {originalComponent.id.slice(-8)})
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* View options */}
          <div className="flex items-center bg-white rounded border">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDiff(!showDiff)}
              className="h-8 px-2"
            >
              {showDiff ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </Button>
          </div>

          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Main editor area */}
        <div className="flex-1 overflow-auto">
          <Tabs defaultValue="fields" className="h-full">
            <TabsList className="w-full">
              <TabsTrigger value="fields">Field by Field</TabsTrigger>
              <TabsTrigger value="preview">Preview</TabsTrigger>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
            </TabsList>

            <TabsContent
              value="fields"
              className="p-4 space-y-4 h-full overflow-auto"
            >
              {changedFields.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-500">
                  No conflicts detected
                </div>
              ) : (
                changedFields.map(renderFieldDiff)
              )}
            </TabsContent>

            <TabsContent value="preview" className="p-4 h-full overflow-auto">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Merged Component Preview
                </h3>
                <pre className="text-xs bg-white p-3 rounded border overflow-auto">
                  {JSON.stringify(mergedComponent, null, 2)}
                </pre>
              </div>
            </TabsContent>

            <TabsContent value="timeline" className="p-4 h-full overflow-auto">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">
                  Change Timeline
                </h3>
                <div className="space-y-3">
                  {conflictedVersions
                    .sort(
                      (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
                    )
                    .map((version, index) => (
                      <div
                        key={version.userId}
                        className="flex items-start gap-3"
                      >
                        <div className="flex flex-col items-center">
                          <UserAvatar user={version.user} size="sm" />
                          {index < conflictedVersions.length - 1 && (
                            <div className="w-px h-8 bg-gray-300 mt-2" />
                          )}
                        </div>
                        <div className="flex-1 bg-gray-50 p-3 rounded">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-sm">
                              {version.user.user?.username || `User ${version.user.userId?.slice(-4)}`}
                            </span>
                            <span className="text-xs text-gray-500">
                              {version.timestamp.toLocaleString()}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600">
                            Modified:{" "}
                            {version.changes.map((c) => c.field).join(", ")}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar with conflict summary */}
        <div className="w-80 border-l bg-gray-50 p-4 overflow-auto">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Conflict Summary
              </h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div>• {conflictedVersions.length} conflicted versions</div>
                <div>• {changedFields.length} changed fields</div>
                <div>
                  • {changedFields.filter(hasFieldConflict).length} fields with
                  conflicts
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Contributors
              </h4>
              <div className="space-y-2">
                {conflictedVersions.map((version) => (
                  <div key={version.userId} className="flex items-center gap-2">
                    <UserAvatar
                      user={version.user}
                      size="sm"
                      showStatus={false}
                    />
                    <div className="text-sm">
                      <div>{version.user.user?.username || `User ${version.user.userId?.slice(-4)}`}</div>
                      <div className="text-xs text-gray-500">
                        {version.changes.length} changes
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick actions */}
            <div className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="w-full justify-start"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Original
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-4 border-t bg-gray-50">
        <div className="text-sm text-gray-600">
          {selectedFields.size} of {changedFields.length} fields resolved
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            onClick={() => onResolve(mergedComponent, "manual")}
            disabled={selectedFields.size < changedFields.length}
          >
            <Check className="w-4 h-4 mr-2" />
            Apply Merge
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

// Backdrop component
export const MergeEditorBackdrop: React.FC<{
  children: React.ReactNode;
  onClose: () => void;
}> = ({ children, onClose }) => (
  <motion.div
    className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={onClose}
  >
    <div onClick={(e) => e.stopPropagation()}>{children}</div>
  </motion.div>
);
